import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, cn, Label, Switch } from '@parrot-software/design-system';
import type { UseFormReturn } from 'react-hook-form-v7';
import { useFormState, useWatch } from 'react-hook-form-v7';

import { styles, EMPLOYEE_FORM_IDS } from '@personnel/presentation/components/form/EmployeeForm/shared';
import type { Store } from '@personnel/domain/entities/Store';

export interface AssignStoresSectionProps {
  form: UseFormReturn<any>;
  stores: Store[];
}

/**
 * Section component for assigning stores to an employee
 *
 * @param {AssignStoresSectionProps} props - Component properties
 * @returns {React.ReactElement} Store assignment section
 */
export const AssignStoresSection = React.memo(function AssignStoresSection({
  form,
  stores,
}: AssignStoresSectionProps): React.ReactElement {
  const { t } = useTranslation('personnel-employees');
  const { control, setValue, getValues, trigger } = form;

  const { errors } = useFormState({ control });

  const [localAllStores, setLocalAllStores] = useState(() => getValues('allStoresAssigned') || false);
  const [localAssignedStores, setLocalAssignedStores] = useState(() => getValues('assignedStores') || []);

  const formAllStores = useWatch({ control, name: 'allStoresAssigned' });
  const formAssignedStores = useWatch({ control, name: 'assignedStores' });

  useEffect(() => {
    if (formAllStores !== undefined) {
      setLocalAllStores(formAllStores || false);
    }
  }, [formAllStores]);

  useEffect(() => {
    if (formAssignedStores !== undefined) {
      setLocalAssignedStores(formAssignedStores || []);
    }
  }, [formAssignedStores]);

  const fullStores = useMemo(() => stores.map((s) => ({ uuid: s.uuid, name: s.name })), [stores]);
  const selectedStoreUuids = useMemo(() => ((localAssignedStores || []) as { uuid: string }[]).map((s) => s.uuid), [
    localAssignedStores,
  ]);

  const onToggleAllStores = async (checked: boolean) => {
    const newAssignedStores = checked ? fullStores : [];
    setLocalAllStores(!!checked);
    setLocalAssignedStores(newAssignedStores);
    setValue('allStoresAssigned', !!checked, { shouldDirty: true, shouldValidate: true });
    setValue('assignedStores', newAssignedStores, { shouldDirty: true, shouldValidate: true });
    await trigger('assignedStores');
  };

  const onToggleAllStoresManual = async (checked: boolean) => {
    const newAssignedStores = checked ? fullStores : [];
    setLocalAssignedStores(newAssignedStores);
    setValue('assignedStores', newAssignedStores, { shouldDirty: true, shouldValidate: true });
    await trigger('assignedStores');
  };

  const onToggleStore = async (storeId: string, checked: boolean) => {
    const current = (localAssignedStores || []) as { uuid: string; name: string }[];
    const exists = current.some((s) => s.uuid === storeId);
    const store = stores.find((s) => s.uuid === storeId);
    const next = checked
      ? exists
        ? current
        : [...current, { uuid: storeId, name: store?.name || '' }]
      : current.filter((s) => s.uuid !== storeId);
    setLocalAssignedStores(next);
    setValue('assignedStores', next, { shouldDirty: true, shouldValidate: true });
    await trigger('assignedStores');
  };

  return (
    <div className={styles.container}>
      <div>
        <h2 className={styles.header.cardTitle}>{t('form.storesSection.title')}</h2>
        <p className={styles.header.description}>{t('form.storesSection.description')}</p>
      </div>

      <div className={styles.row}>
        <div className={styles.rowText}>
          <Label>{t('form.storesSection.labelRadio')}</Label>
          <p className={styles.header.description}>{t('form.storesSection.descriptionRadio')}</p>
        </div>
        <Switch
          checked={localAllStores}
          id={EMPLOYEE_FORM_IDS.ALL_STORES}
          onCheckedChange={(checked) => onToggleAllStores(!!checked)}
        />
      </div>

      <div>
        <div className="flex mb-2">
          <Checkbox
            checked={selectedStoreUuids.length === stores.length}
            className="mr-2"
            disabled={localAllStores}
            onCheckedChange={(checked) => onToggleAllStoresManual(!!checked)}
          />
          <Label>{t('form.storesSection.allStoresCheckbox')}</Label>
        </div>

        <div className={styles.storesGrid}>
          {stores.map((store) => (
            <div className={styles.storeItem} key={store.uuid}>
              <Checkbox
                checked={selectedStoreUuids.includes(store.uuid)}
                disabled={localAllStores}
                id={store.uuid}
                onCheckedChange={(checked) => onToggleStore(store.uuid, !!checked)}
              />
              <Label className="break-word" htmlFor={store.uuid}>
                {store.name}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <p className={cn('text-sm text-muted-foreground', { 'text-destructive': !!errors?.assignedStores })}>
        {t('form.storesSection.required')}
        <span>*</span>
      </p>
    </div>
  );
});
